import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { req, log } from '../utils'; // Assuming this is correctly importing your request utility
import { useParams } from "react-router-dom";
import { getFormattedWeekDates } from "../utils";
import { normalizeJobRecord } from "../utils/jobMachines";

const FETCH_MACHINES_ATTEMPTS = 4;
const FETCH_MACHINES_BACKOFF_MS = 500;

const DataContext = createContext(null);

function DataProvider({ children }) {
  //todo make dynamic from backend
  const adminIds = [1, 999,1094,651, 691,653,1306];
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [jobs, setJobs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const use_cached = false;//flip on and off cache on is for faster dev
  const [scheduleFilter, setScheduleFilter] = useState(() => {
    // Retrieve the filter from local storage or default to 'NOT COMPLETED'
    return localStorage.getItem('_bc_schedule_filter') || 'NOT COMPLETED';
  });
  const [techFilter, setTechFilter] = useState(0);

  useEffect(() => {
    // Store the scheduleFilter in local storage whenever it changes
    localStorage.setItem('_bc_schedule_filter', scheduleFilter);
  }, [scheduleFilter]);

  useEffect(() => {
    //if the account_id is in the adminIds array then set isAdmin to true
    const account_id = localStorage.getItem('_bc_tech_user_id');
    if (account_id && adminIds.includes(parseInt(account_id))) {
      setIsAdmin(true);
    }
  }, []);
  const batchUpdate = useCallback((updates) => {
    updates.forEach(({ objectType, id, property, value }) => {
      if (objectType === "job") {
        setJobs(prevJobs => prevJobs.map(job =>
          String(job.service_call_id) === String(id) ? { ...job, [property]: value } : job
        ));
      } else if (objectType === "machine") {
        setMachines(prevMachines => prevMachines.map(machine =>
          String(machine.machine_id_pk) === String(id) ? { ...machine, [property]: value } : machine
        ));
      }
      // Add more object types as needed
    });
  }, []);

  const updateProperty = useCallback((objectType, id, name, value) => {
    try {
      if (objectType === "job") {
        let job = jobs.find(job => String(job.service_call_id) === String(id));
        const updatedJobs = jobs.map(job => String(job.service_call_id) === String(id) ? { ...job, [name]: value } : job);
        job = updatedJobs.find(job => String(job.service_call_id) === String(id));
        setJobs(updatedJobs);
      } else if (objectType === "machine") {
        const updatedMachines = machines.map(machine => String(machine.machine_id_pk) === String(id) ? { ...machine, [name]: value } : machine);
        setMachines(updatedMachines);
      }

      // Add logic for other object types as necessary
    } catch (error) {
      log("updateProperty", error);
    }
  }, [jobs, machines]);
  const addContextElement = (objectType, data) => {
    if (objectType === "job") {
      setJobs([...jobs, data])
    }
    if (objectType === "venueLocations") {
      setVenueLocations([...venueLocations, data])
    }
  }
  const appendJobLog = useCallback((jobId, logEntry) => {
    try {
      const updatedJobs = jobs.map(job => {
        if (String(job.service_call_id) === String(jobId)) {
          // Ensure job.log is always an array
          const log = Array.isArray(job.log) ? job.log : [];
          return { ...job, log: [...log, logEntry] };
        }
        return job;
      });
      setJobs(updatedJobs);
    } catch (error) {
      log("appendJobLog", error);
    }
  }, [jobs]);
  

  const localStorageKeys = {
    jobs: "_bc_jobs",
    machines: "_bc_machines",
    parts: "_bc_parts",
    schedule: "_bc_schedule",
    venueLocations: "_bc_venueLocations",
    technicians: "_bc_technicians"
  };

  const autoParseJSON = (value) => {
    //if it doesn't look like a JSON object, return the value
    if (typeof value !== 'string' || !value || (!value.trim().startsWith("{") && !value.trim().startsWith("["))) {
      // Return the value directly if it's not a string, empty, or doesn't look like JSON
      return value;
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) || (typeof parsed === 'object' && parsed !== null)) {
        return parsed;
      } else {
        return value; // Fallback to original value if not array or object
      }
    } catch(e) {
      console.warn("Error autoParseJSON", value,e);
      if (value.trim().startsWith("[")) {
        return []; // Fallback to empty array
      } else if (value.trim().startsWith("{")) {
        return {}; // Fallback to empty object
      } else {
        return value; // Fallback to original value
      }
    }
  };

  const processAndSetFetchedData = (key, data) => {
  
    try {
      const processedData = data.map(item => {
        return Object.entries(item).reduce((acc, [propKey, propValue]) => {
          acc[propKey] = typeof propValue === 'string' ? autoParseJSON(propValue) : propValue;
          return acc;
        }, {});
      });

      switch (key) {
        case 'jobs': setJobs(processedData.map(normalizeJobRecord)); break;
        case 'machines': setMachines(processedData); break;
        case 'parts': setParts(processedData); break;
        case 'schedule': setSchedule(processedData); break;
        case 'venueLocations': setVenueLocations(processedData); break;
        case 'technicians': setTechnicians(processedData); break;
        default: break;
      }
    } catch (error) {
      log("processAndSetFetchedData", error);
    }
  };
  const { date } = useParams();
  const account_id = localStorage.getItem('_bc_tech_user_id');
  const startOfWeek = getFormattedWeekDates(date ? date : null)[0];
  const fetchDataAndUpdateState = async (key, endpoint) => {
    try {
      let data;

      if (use_cached) {
        const cachedData = localStorage.getItem(localStorageKeys[key]);
        if (cachedData) {
          data = JSON.parse(cachedData);
          processAndSetFetchedData(key, data); // Use cached data
          return; // Exit the function early as we don't need to fetch from the server
        }
      }

      // If no cached data or use_cached is false, proceed to fetch from the server
      const response = await req('GET', endpoint);
      if (response) {
    
        processAndSetFetchedData(key, response); // Process data before setting state
        const dataString = JSON.stringify(response);

        // Check if data exceeds maximum local storage size
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (dataString.length <= maxSize) {
          localStorage.setItem(localStorageKeys[key], dataString); // Store new value
        } else {
          log("setLocalStorage", `Data size exceeds maximum limit of ${maxSize} bytes`);
        }
      } else {
        throw new Error(`Failed to fetch ${key}`);
      }
    } catch (error) {
      log("fetchDataAndUpdateState", {error,key, endpoint});
    }
  };

  const fetchMachinesWithRetry = useCallback(async (options = {}) => {
    const { skipCache = false } = options;
    const key = "machines";
    const endpoint = "get_machines";

    for (let attempt = 1; attempt <= FETCH_MACHINES_ATTEMPTS; attempt++) {
      try {
        let data;

        if (!skipCache && use_cached) {
          const cachedData = localStorage.getItem(localStorageKeys[key]);
          if (cachedData) {
            data = JSON.parse(cachedData);
            processAndSetFetchedData(key, data);
            return true;
          }
        }

        const response = await req("GET", endpoint);
        if (response) {
          processAndSetFetchedData(key, response);
          const dataString = JSON.stringify(response);
          const maxSize = 5 * 1024 * 1024;
          if (dataString.length <= maxSize) {
            localStorage.setItem(localStorageKeys[key], dataString);
          } else {
            log("setLocalStorage", `Data size exceeds maximum limit of ${maxSize} bytes`);
          }
          return true;
        }
      } catch (error) {
        log("fetchMachinesWithRetry", { attempt, error });
      }
      if (attempt < FETCH_MACHINES_ATTEMPTS) {
        await new Promise((r) =>
          setTimeout(r, FETCH_MACHINES_BACKOFF_MS * attempt)
        );
      }
    }
    return false;
  }, []);

  const refreshMachines = useCallback(
    () => fetchMachinesWithRetry({ skipCache: true }),
    [fetchMachinesWithRetry]
  );

  const fetchScheduleJobs = useCallback(async (targetDate = null) => {
    const [start_date, , , , , , end_date] = getFormattedWeekDates(targetDate || date || null);
    const scheduleParams = {
      start_date,
      end_date,
      include_images: 0,
      status: 'all',
      limit: 250,
    };

    // Keep payload small for non-admins and admin tech filter views.
    if (!isAdmin) {
      if (account_id) scheduleParams.tech_id = account_id;
    } else if (techFilter && Number(techFilter) !== 0) {
      scheduleParams.tech_id = techFilter;
    }

    const normalizeHighlight = (job) => {
      if (job?.highlight) return job.highlight;
      const status = String(job?.status || "").toLowerCase();
      if (status === "in progress") return "red";
      if (status === "in transit") return "orange";
      if (status === "reschedule") return "yellow";
      if (status === "completed") return "purple";
      return "blue";
    };

    const normalizeScheduleJob = (job) =>
      normalizeJobRecord({
        ...job,
        assigned_to: job?.assigned_to ?? job?.tech_id,
        highlight: normalizeHighlight(job),
      });

    try {
      const allItems = [];
      let page = 1;
      let hasMore = false;
      let safetyCounter = 0;

      do {
        const scheduleResponse = await req('GET', 'get_schedule_jobs', { ...scheduleParams, page });
        if (!scheduleResponse) break;
        const items = Array.isArray(scheduleResponse)
          ? scheduleResponse
          : (Array.isArray(scheduleResponse.items) ? scheduleResponse.items : []);
        allItems.push(...items.map(normalizeScheduleJob));

        hasMore = Boolean(scheduleResponse?.has_more);
        if (!hasMore && scheduleResponse?.total && items.length > 0) {
          hasMore = allItems.length < Number(scheduleResponse.total);
        }
        page += 1;
        safetyCounter += 1;
      } while (hasMore && safetyCounter < 20);

      if (allItems.length > 0) {
        processAndSetFetchedData('jobs', allItems);
        setError(false);
        setErrorMessage('');
        return true;
      }
    } catch (error) {
      log("fetchScheduleJobs-new-endpoint", error);
    }

    // Fallback to legacy endpoint while backend rollout is in progress.
    try {
      const legacyResponse = await req('GET', 'get_jobs_with_images', { date: start_date });
      if (legacyResponse) {
        processAndSetFetchedData('jobs', legacyResponse);
        setError(false);
        setErrorMessage('');
        return true;
      }
    } catch (error) {
      log("fetchScheduleJobs-legacy-fallback", error);
    }
    return false;
  }, [account_id, date, isAdmin, techFilter]);

  const fetchReferenceData = useCallback(async () => {
    try {
      await Promise.all([
        fetchMachinesWithRetry(),
        fetchDataAndUpdateState('parts', 'get_parts'),
        fetchDataAndUpdateState('schedule', 'get_schedule'),
        fetchDataAndUpdateState('venueLocations', 'get_venue_locations'),
        fetchDataAndUpdateState('technicians', 'get_technicians')
      ]);
    } catch (error) {
      log("fetchReferenceData", error);
    }
  }, [fetchMachinesWithRetry]);

  useEffect(() => {
    let isCancelled = false;
    const loadScheduleJobs = async () => {
      setLoading(true);
      const ok = await fetchScheduleJobs(date || null);
      if (!ok && !isCancelled) {
        setError(true);
        setErrorMessage('Failed to fetch schedule');
      }
      if (!isCancelled) {
        setLoading(false);
      }
    };

    loadScheduleJobs();
    return () => {
      isCancelled = true;
    };
  }, [date, isAdmin, techFilter, fetchScheduleJobs]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    const handleOnlineStatusChange = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, []);

  const updateScheduleForDate = useCallback(async (date) => {
    setLoading(true);
    try {
      const success = await fetchScheduleJobs(date);
      if (!success) {
        throw new Error('Failed to fetch schedule');
      }
    } catch (error) {
      log("updateScheduleForDate", error);
      setError(true);
      setErrorMessage('Failed to fetch schedule');
    } finally {
      setLoading(false);
    }
  }, [fetchScheduleJobs]);

  const contextValue = {
    isOnline,
    loading,
    error,
    errorMessage,
    jobs,
    setJobs,
    machines,
    setMachines,
    parts,
    setParts,
    schedule,
    setSchedule,
    venueLocations,
    setVenueLocations,
    updateProperty,
    appendJobLog, 
    addContextElement, 
    startOfWeek,
    batchUpdate,
    scheduleFilter, 
    setScheduleFilter, 
    isAdmin,
    technicians,
    setTechFilter,
    techFilter,
    updateScheduleForDate,
    refreshMachines,
  };

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
}

function useDataContext() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
}

export { DataProvider, useDataContext };
